import { Component, EventEmitter, inject, Input, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from '../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { AccountFilterData } from '../../../../../_shared/models';
import { ReviewItemBase, ReviewSuggestion, ReviewTab } from '../../../models/excel-review.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-excel-review-modal',
  standalone: true,
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    CommonModule,
    AccountFilterComponent,
    SearchComponent
  ],
  templateUrl: './excel-review-modal.component.html',
  styleUrl: './excel-review-modal.component.scss'
})
export class ExcelReviewModalComponent<T extends ReviewItemBase> {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  @Input() title = '';
  @Input() subtitle = '';
  @Input() items: T[] = [];
  @Input() selected: T | null = null;
  @Output() selectedChange = new EventEmitter<T | null>();

  @Input() isLoading = false;
  @Input() isImporting = false;
  @Input() canImport = false;
  @Input() importLabel = '';
  @Input() serverErrors: string[] = [];

  @Input() searchPlaceholder = '';
  @Input() searchWidth = '400px';
  /** Decides whether an item matches the (already lower-cased, trimmed) search term. */
  @Input() matchesSearch: (item: T, term: string) => boolean = () => true;

  @Input() showAccountFilter = false;
  @Input() accountFilter: AccountFilterData = { accountIds: [] };
  @Input() accountFilterPlaceholder = '';
  @Output() accountFilterChanged = new EventEmitter<AccountFilterData>();

  /** Singular, lower-case entity name used in the delete confirmation ("compliance", "part"). */
  @Input() entityLabel = '';
  @Input() itemName: (item: T) => string = () => '';
  @Input() deleteWarningMessage = '';

  /** Renders the `<th>` cells between the status-icon column and the Status column. */
  @Input() headerTemplate: TemplateRef<unknown> | null = null;
  /** Renders the `<td>` cells for one item; context `$implicit` is the item. */
  @Input() rowTemplate: TemplateRef<unknown> | null = null;
  /** Number of `<td>` cells the row template renders (used for the empty-state colspan). */
  @Input() columnCount = 0;
  /** Renders the editable fields in the detail panel; context `$implicit` is the selected item. */
  @Input() detailTemplate: TemplateRef<unknown> | null = null;

  @Input() emptyText = '';
  @Input() detailEmptyText = '';

  /** Emitted after an item was removed from `items`; re-validate the remaining items here. */
  @Output() itemRemoved = new EventEmitter<T>();
  /** Emitted when the user clicks "Apply Suggestion" for the selected item. */
  @Output() applySuggestions = new EventEmitter<T>();
  @Output() importRequested = new EventEmitter<void>();
  @Output() closeRequested = new EventEmitter<void>();

  private modalService = inject(NgbModal);

  activeTab: ReviewTab = 'all';
  searchTerm = '';

  get errorCount(): number {
    return this.items.filter(item => item.errors.length > 0).length;
  }

  get filteredItems(): T[] {
    const term = this.searchTerm.trim().toLowerCase();
    return this.items.filter(item => {
      if (this.activeTab === 'error' && item.errors.length === 0) {
        return false;
      }
      return !term || this.matchesSearch(item, term);
    });
  }

  get fixedColumnCount(): number {
    // status icon + Status + delete
    return 3;
  }

  setTab(tab: ReviewTab): void {
    this.activeTab = tab;
  }

  onSearch(term: string): void {
    this.searchTerm = term;
  }

  select(item: T): void {
    this.selected = item;
    this.selectedChange.emit(item);
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilterChanged.emit(filter);
  }

  removeItem(item: T): void {
    const name = this.itemName(item);
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      windowClass: 'confirmation-modal__content'
    });

    modalRef.componentInstance.data = {
      title: this.transloco.translate('assetShared.excelReview.deleteTitle', { entity: this.capitalize(this.entityLabel || this.transloco.translate('assetShared.excelReview.entityLabel')) }),
      message: this.transloco.translate('assetShared.excelReview.deleteMessage', { name: name || this.transloco.translate('assetShared.excelReview.deleteThisEntity', { entity: this.entityLabel || this.transloco.translate('assetShared.excelReview.entityLabel') }) }),
      warningMessage: this.deleteWarningMessage,
      confirmationPrompt: 'delete',
      confirmPlaceholder: this.transloco.translate('serviceLibrary.delete.confirmPlaceholder'),
      confirmText: this.transloco.translate('serviceLibrary.delete.confirmText'),
      declineText: this.transloco.translate('serviceLibrary.delete.declineText')
    };

    modalRef.closed.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteItem(item);
      }
    });
  }

  private deleteItem(item: T): void {
    const index = this.items.indexOf(item);
    if (index < 0) return;
    this.items.splice(index, 1);
    this.itemRemoved.emit(item);
    if (this.selected === item) {
      const next = this.items.find(other => other.errors.length > 0) ?? this.items[0] ?? null;
      this.selected = next;
      this.selectedChange.emit(next);
    }
  }

  warningTitle(item: T): string {
    const hasMissing = item.errors.some(error => error.kind === 'missing');
    const hasIncorrect = item.errors.some(error => error.kind === 'incorrect');
    if (hasMissing && hasIncorrect) return this.transloco.translate('assetShared.excelReview.missingAndIncorrect');
    return this.transloco.translate(hasIncorrect ? 'assetShared.excelReview.incorrect' : 'assetShared.excelReview.missing');
  }

  suggestions(item: T): ReviewSuggestion[] {
    return item.errors
      .map(error => error.suggestion)
      .filter((suggestion): suggestion is ReviewSuggestion => !!suggestion);
  }

  onApplySuggestions(): void {
    if (!this.selected) return;
    this.applySuggestions.emit(this.selected);
  }

  private capitalize(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
  }
}
